(() => {
  'use strict';

  const STORAGE_KEY = 'kokanee-age-verified-until-v1';
  const DURATION = 60 * 60 * 1000; // One hour.

  const MIN_AGES = {
    AB: 18,
    BC: 19,
    MB: 18,
    NB: 19,
    NL: 19,
    NT: 19,
    NS: 19,
    NU: 19,
    ON: 19,
    PE: 19,
    QC: 18,
    SK: 19,
    YT: 19
  };

  const MESSAGES = {
    en: {
      province: 'Please select your province or territory.',
      date: 'Please enter a valid, complete birth date.',
      underage:
        'You must be {age} or older to enter this site in your province or territory.'
    },
    fr: {
      province: 'Veuillez choisir votre province ou territoire.',
      date: 'Veuillez entrer une date de naissance complète et valide.',
      underage:
        'Vous devez avoir au moins {age} ans pour accéder à ce site dans votre province ou territoire.'
    }
  };

  function parseBirthDate(year, month, day, today = new Date()) {
    if (
      !/^\d{4}$/.test(year) ||
      !/^\d{1,2}$/.test(month) ||
      !/^\d{1,2}$/.test(day)
    ) {
      return null;
    }

    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    const birth = new Date(y, m - 1, d);

    // Reject impossible dates, such as February 30.
    if (
      birth.getFullYear() !== y ||
      birth.getMonth() !== m - 1 ||
      birth.getDate() !== d
    ) {
      return null;
    }

    if (birth > today || today.getFullYear() - y > 130) {
      return null;
    }

    return birth;
  }

  function ageOn(birth, today = new Date()) {
    let age = today.getFullYear() - birth.getFullYear();

    if (
      today.getMonth() < birth.getMonth() ||
      (
        today.getMonth() === birth.getMonth() &&
        today.getDate() < birth.getDate()
      )
    ) {
      age--;
    }

    return age;
  }

  function validExpiry(value) {
    const expiry = Number(value);
    const now = Date.now();

    return (
      Number.isFinite(expiry) &&
      expiry > now &&
      expiry <= now + DURATION
    ) ? expiry : 0;
  }

  function init() {
    // Test the gate through the storefront Preview.
    // Keep it hidden inside Shopify's visual theme editor.
    if (window.Shopify?.designMode) return;

    const path = window.location.pathname
      .replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '')
      .replace(/\/$/, '');

    const excludedPaths = [
      '/policies/privacy-policy',
      '/policies/terms-of-service',
      '/pages/privacy-policy',
      '/pages/terms-of-use'
    ];

    if (excludedPaths.includes(path)) return;

    const gate = document.querySelector('[data-age-gate]');

    if (!gate || gate.dataset.initialized === 'true') return;

    const form = gate.querySelector('[data-age-gate-form]');
    const province = gate.querySelector('[data-age-gate-province]');
    const year = gate.querySelector('[data-age-gate-year]');
    const month = gate.querySelector('[data-age-gate-month]');
    const day = gate.querySelector('[data-age-gate-day]');
    const error = gate.querySelector('[data-age-gate-error]');

    if (!form || !province || !year || !month || !day || !error) {
      return;
    }

    gate.dataset.initialized = 'true';

    let expiresAt = 0;
    let timer;
    let open = false;
    let previousFocus;
    let previousOverflow;
    let errorKey = '';
    let errorAge = '';

    try {
      expiresAt = validExpiry(localStorage.getItem(STORAGE_KEY));
    } catch (_) {
      // The gate can still work when browser storage is unavailable.
    }

    function renderError() {
      const language =
        gate.querySelector('[data-age-gate-language]')?.value ||
        document.documentElement.lang;

      const messages = MESSAGES[/^fr\b/i.test(language) ? 'fr' : 'en'];

      error.textContent = errorKey
        ? messages[errorKey].replace('{age}', String(errorAge))
        : '';

      error.classList.toggle('is-visible', Boolean(errorKey));
    }

    function showError(key, input, requiredAge = '') {
      errorKey = key;
      errorAge = requiredAge;
      renderError();
      input.focus();
    }

    function focusable() {
      return [
        ...gate.querySelectorAll(
          'button, a[href], input, select, textarea, [tabindex]'
        )
      ].filter(element =>
        !element.disabled &&
        element.tabIndex >= 0 &&
        element.getClientRects().length
      );
    }

    function setOpen(value) {
      gate.hidden = !value;
      gate.classList.toggle('is-active', value);
      gate.setAttribute('aria-hidden', String(!value));

      if (open === value) return;

      open = value;

      if (open) {
        previousFocus = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';

        (focusable()[0] || gate).focus();
      } else {
        document.documentElement.style.overflow = previousOverflow;

        form.reset();
        errorKey = '';
        renderError();

        if (previousFocus?.isConnected) {
          previousFocus.focus();
        }
      }
    }

    function sync() {
      clearTimeout(timer);

      const remaining = expiresAt - Date.now();

      setOpen(remaining <= 0);

      if (remaining > 0) {
        timer = setTimeout(sync, remaining);
      }
    }

    [year, month, day].forEach((input, index) => {
      input.addEventListener('input', () => {
        input.value = input.value
          .replace(/\D/g, '')
          .slice(0, index === 0 ? 4 : 2);
      });
    });

    form.addEventListener('input', () => {
      errorKey = '';
      renderError();
    });

    province.addEventListener('change', () => {
      errorKey = '';
      renderError();
    });

    gate.querySelector('[data-age-gate-language]')
      ?.addEventListener('change', renderError);

    form.addEventListener('submit', event => {
      event.preventDefault();

      const requiredAge = MIN_AGES[province.value];

      if (typeof requiredAge !== 'number') {
        return showError('province', province);
      }

      const today = new Date();

      const birth = parseBirthDate(
        year.value.trim(),
        month.value.trim(),
        day.value.trim(),
        today
      );

      if (!birth) {
        return showError('date', year);
      }

      if (ageOn(birth, today) < requiredAge) {
        return showError('underage', year, requiredAge);
      }

      expiresAt = Date.now() + DURATION;

      // Remember only the expiry time, never the birth date.
      try {
        localStorage.setItem(STORAGE_KEY, String(expiresAt));
      } catch (_) {
        // Without storage, verification lasts on this page only.
      }

      sync();
    });

    // Keep keyboard navigation inside the gate while it is open.
    gate.addEventListener('keydown', event => {
      if (!open || event.key !== 'Tab') return;

      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];

      if (!first) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('focusin', event => {
      if (open && !gate.contains(event.target)) {
        (focusable()[0] || gate).focus();
      }
    });

    // Synchronize verification between browser tabs.
    window.addEventListener('storage', event => {
      if (event.key === STORAGE_KEY || event.key === null) {
        expiresAt = validExpiry(event.newValue);
        sync();
      }
    });

    window.addEventListener('pageshow', sync);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) sync();
    });

    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(); 